import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Gallery, Image } from "react-grid-gallery";
import { images, CustomImage } from "./gallery-images";
import "../components/CSS/ImageGallery.css";
import { useTranslation } from 'react-i18next';

export interface CustomizedImage extends Image {
  original: string;
  caption: string;
  tags?: { value: string; title: string }[];
}

export default function ImageGallery() {
  const [index, setIndex] = useState(-1);
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [isLoaded, setIsLoaded] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [clickPosition, setClickPosition] = useState<{ x: number; y: number } | null>(null);
  const { t, i18n } = useTranslation();
  const lightboxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // Reset selected category to "All" when language changes
  useEffect(() => {
    setSelectedCategory("All");
  }, [i18n.language]);

  // Get unique categories from original images (translation keys)
  const categoryKeys: string[] = ["All", ...Array.from(new Set(
    images.flatMap(img => img.tags?.map(tag => tag.value).filter((val): val is string => typeof val === 'string') || [])
  ))];

  // Translate captions and tags
  const translatedImages = images.map((image) => ({
    ...image,
    caption: typeof image.caption === 'string' ? t(image.caption) : '',
    tags: image.tags?.map((tag) => ({
      ...tag,
      value: typeof tag.value === 'string' ? t(tag.value) : '',
      title: typeof tag.title === 'string' ? t(tag.title) : ''
    }))
  }));

  // Filter images by category (using original tag values as keys)
  const filteredImages = selectedCategory === "All"
    ? translatedImages
    : translatedImages.filter((img, index) =>
        images[index].tags?.some(tag => tag.value === selectedCategory)
      );

  const currentImage = filteredImages[index];

  const handleClick = (clickIndex: number, _item: CustomImage, event: any) => {
    // Get click position for animation origin
    const rect = event.target.getBoundingClientRect();
    setClickPosition({
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2
    });
    setIndex(clickIndex);
  };

  const handleClose = useCallback(() => {
    setIndex(-1);
    setClickPosition(null);
  }, []);

  const handleMovePrev = useCallback(() => {
    setIndex((currentIndex) => {
      const currentFilteredLength = filteredImages.length;
      return (currentIndex + currentFilteredLength - 1) % currentFilteredLength;
    });
  }, [filteredImages.length]);

  const handleMoveNext = useCallback(() => {
    setIndex((currentIndex) => {
      const currentFilteredLength = filteredImages.length;
      return (currentIndex + 1) % currentFilteredLength;
    });
  }, [filteredImages.length]);

  const handleZoomIn = () => {
    setZoomLevel(prev => {
      const newZoom = Math.min(prev + 0.25, 3);
      return newZoom;
    });
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => {
      const newZoom = Math.max(prev - 0.25, 0.5);
      return newZoom;
    });
  };

  const handleZoomReset = () => {
    setZoomLevel(1);
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (index === -1) return;

      if (e.key === 'Escape') handleClose();
      else if (e.key === 'ArrowRight') handleMoveNext();
      else if (e.key === 'ArrowLeft') handleMovePrev();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [index, handleClose, handleMoveNext, handleMovePrev]);

  // Reset zoom when image changes
  useEffect(() => {
    if (index !== -1) {
      setZoomLevel(1);
    }
  }, [index]);

  useEffect(() => {
    const body = document.body;

    if (index !== -1) {
      body.classList.add('lightbox-active');
    } else {
      body.classList.remove('lightbox-active');
    }

    return () => {
      body.classList.remove('lightbox-active');
    };
  }, [index]);

  return (
    <div className={`gallery-page ${isLoaded ? 'loaded' : ''} ${index !== -1 ? 'lightbox-active' : ''}`}>
      <div className="gallery-header">
        <h1 className="gallery-title">
          {t("Gallery")}
        </h1>
        <p className="gallery-subtitle">
          {t("GallerySubtitle")}
        </p>
      </div>

      <div className="gallery-filters">
        {categoryKeys.map((category: string) => (
          <button
            key={category}
            className={`filter-btn ${selectedCategory === category ? 'active' : ''}`}
            onClick={() => {
              setSelectedCategory(category);
              setIndex(-1);
            }}
          >
            {t(category)}
          </button>
        ))}
      </div>

      <div className="gallery-count">
        {t("PhotoCount", { count: filteredImages.length })}
      </div>

      <div className="gallery-grid-wrapper">
        <Gallery
          images={filteredImages}
          onClick={handleClick}
          enableImageSelection={false}
          margin={6}
          rowHeight={200}
          maxRows={0}
          tagStyle={{
            color: 'white',
            backgroundColor: 'black',
            fontSize: '0.75rem',
            fontWeight: '600',
            padding: '0.4em 0.8em',
            borderRadius: '20px',
            backdropFilter: 'blur(4px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            textTransform: 'capitalize',
            letterSpacing: '0.5px'
          }}
        />
      </div>

      {index !== -1 && currentImage && createPortal(
        <div
          className="custom-lightbox"
          onClick={(e) => {
            if (e.target === e.currentTarget) handleClose();
          }}
          ref={lightboxRef}
          style={{
            '--click-x': clickPosition ? `${clickPosition.x}px` : '50vw',
            '--click-y': clickPosition ? `${clickPosition.y}px` : '50vh'
          } as React.CSSProperties}
        >
          <div className="lightbox-backdrop" onClick={handleClose} />

          <div className="lightbox-content">
            {/* Close Button */}
            <button
              className="lightbox-btn close-btn"
              onClick={handleClose}
              aria-label="Close"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>

            {/* Navigation Buttons */}
            <button
              className="lightbox-btn nav-btn prev-btn"
              onClick={handleMovePrev}
              aria-label="Previous"
            >
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>

            <button
              className="lightbox-btn nav-btn next-btn"
              onClick={handleMoveNext}
              aria-label="Next"
            >
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>

            {/* Zoom Controls */}
            <div className="zoom-controls">
              <button
                className="lightbox-btn zoom-btn"
                onClick={handleZoomOut}
                disabled={zoomLevel <= 0.5}
                aria-label="Zoom out"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="8" y1="11" x2="14" y2="11"></line>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </button>

              <button
                className="lightbox-btn zoom-btn"
                onClick={handleZoomReset}
                disabled={zoomLevel === 1}
                aria-label="Reset zoom"
              >
                <span className="zoom-level">{Math.round(zoomLevel * 100)}%</span>
              </button>

              <button
                className="lightbox-btn zoom-btn"
                onClick={handleZoomIn}
                disabled={zoomLevel >= 3}
                aria-label="Zoom in"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="11" y1="8" x2="11" y2="14"></line>
                  <line x1="8" y1="11" x2="14" y2="11"></line>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </button>
            </div>

            {/* Main Image */}
            <div className="lightbox-image-container">
              <div
                className="lightbox-image-frame"
                style={{
                  transform: `scale(${zoomLevel})`,
                }}
              >
                <img
                  src={currentImage.original}
                  alt={currentImage.caption}
                  className="lightbox-image"
                />

                {/* Image Info */}
                <div className="lightbox-info">
                  <div className="lightbox-caption">{currentImage.caption}</div>
                  {currentImage.tags && currentImage.tags.length > 0 && (
                    <div className="lightbox-tags">
                      {currentImage.tags.map((tag, idx) => (
                        <span key={idx} className="lightbox-tag">
                          {tag.title}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="lightbox-counter">
                    {index + 1} / {filteredImages.length}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
